import { SupabaseClient } from '@supabase/supabase-js';
import { ValidationError } from '@repo/errors';
import { supabase as supabaseClient } from '../client/browser';
import { v4 as uuidv4 } from 'uuid';

export interface ProgressNoteData {
  sessionId?: string;
  episodeId?: string; // For individual therapy
  groupId?: string; // For couples/group therapy
  noteDate: string;
  noteType:
    | 'SESSION_NOTE'
    | 'PHONE_NOTE'
    | 'CRISIS_NOTE'
    | 'COLLATERAL'
    | 'SUPERVISION'
    | 'CORRECTION'
    | 'CONTACT_NOTE'
    | 'RISK_ASSESSMENT'
    | 'SAFETY_PLAN'
    | 'DISCHARGE_SUMMARY'
    | 'ATTENDANCE_LETTER'
    | 'TREATMENT_SUMMARY'
    | 'DUTY_TO_REPORT'
    | 'RELEASE_OF_INFO'
    | 'OTHER'
    | 'LETTER_OF_SUPPORT'
    | 'CARE_APPLICATION';
  status: 'DRAFT' | 'SUBMITTED';
  noteContent: any; // Flexible JSON structure to support different templates
  proId: string;
  signed: boolean;
  signedAt?: string;
}

export interface ProgressNote extends ProgressNoteData {
  id: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Create a new progress note
 */
export async function createProgressNote(
  data: ProgressNoteData,
  supabase?: SupabaseClient
): Promise<ProgressNote> {
  const client = supabase || supabaseClient;
  
  const now = new Date().toISOString();
  
  // Debug logging
  console.log('[createProgressNote] Creating note with data:', {
    sessionId: data.sessionId,
    episodeId: data.episodeId,
    groupId: data.groupId,
    noteType: data.noteType,
    status: data.status
  });
  
  const { data: note, error } = await client
    .from('progress_notes')
    .insert({
      id: uuidv4(),
      sessionId: data.sessionId,
      episodeId: data.episodeId,
      groupId: data.groupId,
      noteDate: data.noteDate,
      noteType: data.noteType,
      status: data.status,
      noteContent: data.noteContent,
      proId: data.proId,
      signed: data.signed,
      signedAt: data.signedAt,
      createdAt: now,
      updatedAt: now
    })
    .select()
    .single();
  
  if (error) {
    console.error('Error creating progress note:', error);
    throw error;
  }

  // Debug logging to confirm what was saved
  console.log('[createProgressNote] Successfully created note:', {
    noteId: note.id,
    sessionId: note.sessionId,
    episodeId: note.episodeId,
    groupId: note.groupId,
    status: note.status,
    createdAt: note.createdAt,
    updatedAt: note.updatedAt
  });

  // If this is a submitted discharge summary, mark the episode(s) and group as COMPLETED
  if (data.status === 'SUBMITTED' && data.noteContent?.templateType === 'DISCHARGE_SUMMARY') {
    console.log('[createProgressNote] Discharge summary submitted, marking episode(s) as COMPLETED');
    console.log('[createProgressNote] Data received:', {
      hasGroupId: !!data.groupId,
      groupId: data.groupId,
      hasEpisodeId: !!data.episodeId,
      episodeId: data.episodeId,
      status: data.status,
      templateType: data.noteContent?.templateType
    });

    // For couples/group therapy (groupId takes priority)
    if (data.groupId) {
      // Mark the therapy group as COMPLETED
      const { error: groupError } = await client
        .from('therapy_groups')
        .update({ status: 'COMPLETED' })
        .eq('id', data.groupId);

      if (groupError) {
        console.error('[createProgressNote] Error marking therapy group as COMPLETED:', groupError);
      } else {
        console.log('[createProgressNote] Therapy group marked as COMPLETED:', data.groupId);
      }

      // Mark all episodes in this group as COMPLETED
      const { error: episodesError } = await client
        .from('episodes')
        .update({
          status: 'COMPLETED',
          endDate: new Date().toISOString()
        })
        .eq('groupId', data.groupId);

      if (episodesError) {
        console.error('[createProgressNote] Error marking group episodes as COMPLETED:', episodesError);
      } else {
        console.log('[createProgressNote] All episodes in group marked as COMPLETED');
      }
    }
    // For individual therapy (episodeId only, when no groupId)
    else if (data.episodeId) {
      const { error: episodeError } = await client
        .from('episodes')
        .update({
          status: 'COMPLETED',
          endDate: new Date().toISOString()
        })
        .eq('id', data.episodeId);

      if (episodeError) {
        console.error('[createProgressNote] Error marking episode as COMPLETED:', episodeError);
      } else {
        console.log('[createProgressNote] Episode marked as COMPLETED:', data.episodeId);
      }
    }
  }

  return note;
}

/**
 * Update an existing progress note
 */
export async function updateProgressNote(
  id: string,
  data: Partial<ProgressNoteData>,
  supabase?: SupabaseClient
): Promise<ProgressNote> {
  const client = supabase || supabaseClient;
  
  // IMPORTANT: Check if note is already signed/submitted (Canadian standards compliance)
  const { data: existingNote, error: fetchError } = await client
    .from('progress_notes')
    .select('status, signed')
    .eq('id', id)
    .single();
  
  if (fetchError) {
    console.error('Error fetching progress note for update:', fetchError);
    throw fetchError;
  }
  
  // Prevent editing of signed/submitted notes per Canadian therapy standards
  if (existingNote.status === 'SUBMITTED' || existingNote.signed) {
    throw new ValidationError('Cannot edit signed or submitted progress notes. Per Canadian therapy documentation standards, signed notes are immutable. Please create an addendum or correction note instead.');
  }
  
  const { data: note, error } = await client
    .from('progress_notes')
    .update({
      ...data,
      updatedAt: new Date().toISOString()
    })
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error updating progress note:', error);
    throw error;
  }

  console.log('[updateProgressNote] Updated note timestamps:', {
    noteId: id,
    updatedAt: note.updatedAt,
    rawData: note
  });

  // If this is a discharge summary being submitted, mark the episode(s) and group as COMPLETED
  if (data.status === 'SUBMITTED' && (data.noteContent?.templateType === 'DISCHARGE_SUMMARY' || note.noteContent?.templateType === 'DISCHARGE_SUMMARY')) {
    console.log('[updateProgressNote] Discharge summary submitted, marking episode(s) as COMPLETED');

    // For couples/group therapy (groupId takes priority)
    if (note.groupId) {
      // Mark the therapy group as COMPLETED
      const { error: groupError } = await client
        .from('therapy_groups')
        .update({ status: 'COMPLETED' })
        .eq('id', note.groupId);

      if (groupError) {
        console.error('[updateProgressNote] Error marking therapy group as COMPLETED:', groupError);
      } else {
        console.log('[updateProgressNote] Therapy group marked as COMPLETED:', note.groupId);
      }

      // Mark all episodes in this group as COMPLETED
      const { error: episodesError } = await client
        .from('episodes')
        .update({
          status: 'COMPLETED',
          endDate: new Date().toISOString()
        })
        .eq('groupId', note.groupId);

      if (episodesError) {
        console.error('[updateProgressNote] Error marking group episodes as COMPLETED:', episodesError);
      } else {
        console.log('[updateProgressNote] All episodes in group marked as COMPLETED');
      }
    }
    // For individual therapy (episodeId only, when no groupId)
    else if (note.episodeId) {
      const { error: episodeError } = await client
        .from('episodes')
        .update({
          status: 'COMPLETED',
          endDate: new Date().toISOString()
        })
        .eq('id', note.episodeId);

      if (episodeError) {
        console.error('[updateProgressNote] Error marking episode as COMPLETED:', episodeError);
      } else {
        console.log('[updateProgressNote] Episode marked as COMPLETED:', note.episodeId);
      }
    }
  }

  return note;
}

/**
 * Get a progress note by ID
 */
export async function getProgressNote(
  id: string,
  supabase?: SupabaseClient
): Promise<ProgressNote | null> {
  const client = supabase || supabaseClient;
  
  const { data: note, error } = await client
    .from('progress_notes')
    .select('*')
    .eq('id', id)
    .single();
  
  if (error) {
    if (error.code === 'PGRST116') {
      // No rows found
      return null;
    }
    console.error('Error fetching progress note:', error);
    throw error;
  }
  
  return note;
}

/**
 * Get progress note by session ID
 */
export async function getProgressNoteBySessionId(
  sessionId: string,
  supabase?: SupabaseClient
): Promise<ProgressNote | null> {
  const client = supabase || supabaseClient;
  
  const { data: note, error } = await client
    .from('progress_notes')
    .select('*')
    .eq('sessionId', sessionId)
    .single();
  
  if (error) {
    if (error.code === 'PGRST116') {
      // No rows found
      return null;
    }
    console.error('Error fetching progress note by session:', error);
    throw error;
  }
  
  return note;
}

/**
 * Get all progress notes for an episode (individual therapy)
 */
export async function getProgressNotesByEpisode(
  episodeId: string,
  supabase?: SupabaseClient
): Promise<ProgressNote[]> {
  const client = supabase || supabaseClient;
  
  const { data: notes, error } = await client
    .from('progress_notes')
    .select('*')
    .eq('episodeId', episodeId)
    .order('noteDate', { ascending: false });
  
  if (error) {
    console.error('Error fetching progress notes for episode:', error);
    throw error;
  }
  
  return notes || [];
}

/**
 * Get all progress notes for a therapy group (couples/group therapy)
 */
export async function getProgressNotesByGroup(
  groupId: string,
  supabase?: SupabaseClient
): Promise<ProgressNote[]> {
  const client = supabase || supabaseClient;
  
  const { data: notes, error } = await client
    .from('progress_notes')
    .select('*')
    .eq('groupId', groupId)
    .order('noteDate', { ascending: false });
  
  if (error) {
    console.error('Error fetching progress notes for group:', error);
    throw error;
  }
  
  return notes || [];
}

/**
 * Delete a progress note (only if in DRAFT status)
 */
export async function deleteProgressNote(
  id: string,
  supabase?: SupabaseClient
): Promise<void> {
  const client = supabase || supabaseClient;
  
  // First check if the note is in DRAFT status
  const { data: note, error: fetchError } = await client
    .from('progress_notes')
    .select('status')
    .eq('id', id)
    .single();
  
  if (fetchError) {
    console.error('Error fetching progress note for deletion:', fetchError);
    throw fetchError;
  }
  
  if (note.status !== 'DRAFT') {
    throw new ValidationError('Cannot delete submitted progress notes');
  }
  
  const { error } = await client
    .from('progress_notes')
    .delete()
    .eq('id', id);
  
  if (error) {
    console.error('Error deleting progress note:', error);
    throw error;
  }
}